// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import usePromotionCodeForm from "@insite/client-framework/Common/Hooks/usePromotionCodeForm";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canApplyPromotionsToCart,
    getCartState,
    getCurrentCartState,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import applyPromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyPromotion";
import removePromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotion";
import removePromotions from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotions";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@keller/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const promotions = promotionsDataView.value;
    const {
        isApplyingPromotion,
        promotionErrorMessage,
        promotionSuccessMessage,
        isCheckingOutWithPayPay,
        isPromotionAppliedSuccess,
        isPromotionRemovedSuccess,
        isPromotionsRemovedSuccess,
    } = state.pages.checkoutReviewAndSubmit;
    return {
        promotions,
        isApplyButtonDisabled: cartState.isLoading || isApplyingPromotion || isCartEmpty(cart),
        canApplyPromosToCart: canApplyPromotionsToCart(cart) && !isCheckingOutWithPayPay,
        promotionErrorMessage,
        promotionSuccessMessage,
        isPromotionAppliedSuccess,
        isPromotionRemovedSuccess,
        isPromotionsRemovedSuccess,
    };
};

const mapDispatchToProps = {
    applyPromotion,
    removePromotion,
    removePromotions,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitPromotionCodeStyles {
    form?: InjectableCss;
    promotionCodeTextField?: TextFieldPresentationProps;
    applyButton?: ButtonPresentationProps;
    applySuccessText?: TypographyPresentationProps;
    promotionRemovedText?: TypographyPresentationProps;
    gridContainer?: GridContainerProps;
    promotionCodeGridItem?: GridItemProps;
    applyPromotionGridItem?: GridItemProps;
    promotionAppliedGridItem?: GridItemProps;
    promotionRemovedGridItem?: GridItemProps;
    removeCodesGridItem?: GridItemProps;
    removeCodeGridItem?: GridItemProps;
    removeCodesLink?: LinkPresentationProps;
    removeCodeTag?: TagPresentationProps;
    labelTypography?: TypographyPresentationProps;
    errorTypography?: TypographyPresentationProps;
    formWrapper?: InjectableCss;
}

export const promotionCodeFormStyles: CheckoutReviewAndSubmitPromotionCodeStyles = {
    form: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    promotionCodeTextField: {
        cssOverrides: {
            formField: css`
                & input:not(.react-datetime-picker__inputGroup__input) {
                    &,
                    &:not(:disabled):hover,
                    &:not(:disabled):focus {
                        outline: none;
                        border: none;
                        box-shadow: none;
                    }
                }
            `,
        },
    },
    applyButton: {
        variant: "tertiary",
        css: css`
            width: 100%;
            margin-top: 2.5em;
        `,
    },
    promotionCodeGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            margin-top: 10px;
        `,
    },
    applyPromotionGridItem: {
        width: [3, 3, 3, 3, 3],
        css: css`
            width: 100%;
            display: flex;
            align-items: flex-start;
            margin-top: 23px !important;
        `,
    },
    applySuccessText: {
        color: "success",
        css: css`
            margin-top: 10px;
        `,
        weight: "bold",
    },
    labelTypography: {
        size: 12,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 13px;
            margin-bottom: 8px;
        `,
    },
    errorTypography: {
        size: 12,
        weight: "bold",
        color: "danger",
        css: css`
            letter-spacing: 0;
            line-height: 13px;
            margin-top: 10px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding-right: 2px;
            border: 1px solid ${color("common.border")};
            border-radius: 1px;
            background-color: ${color("common.white")};
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

            &:hover:not(:disabled) {
                border-color: ${color("common.ui1")};
            }
        `,
    },
};

const styles = promotionCodeFormStyles;
const StyledForm = getStyledWrapper("form");

const CheckoutReviewAndSubmitPromotionCode = ({
    promotions,
    isApplyButtonDisabled,
    promotionSuccessMessage,
    promotionErrorMessage,
    isPromotionAppliedSuccess,
    isPromotionRemovedSuccess,
    isPromotionsRemovedSuccess,
    canApplyPromosToCart,
    applyPromotion,
    removePromotion,
    removePromotions,
}: Props) => {
    if (!canApplyPromosToCart) {
        return null;
    }

    const { promotionCode, setPromotionCode, promotionsWithCode, removingPromotionCode, setRemovingPromotionCode } =
        usePromotionCodeForm(promotions);

    const handlePromotionCodeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        setPromotionCode(event.currentTarget.value);

    const applyPromotionHandler = () => {
        applyPromotion({ promotionCode: promotionCode.trim() });
        setPromotionCode("");
    };

    const removePromotionCodesHandler = () => {
        removePromotions();
    };
    const removePromotionHandler = (promotion: PromotionModel) => {
        removePromotion({ promotionId: promotion.id });
        setRemovingPromotionCode(promotion.promotionCode);
    };

    if (!canApplyPromosToCart) {
        return null;
    }

    const handleSubmit = (event: React.MouseEvent<HTMLFormElement, MouseEvent>) => {
        event.preventDefault();
        applyPromotion({ promotionCode });
    };

    return (
        <>
            <StyledForm {...styles.form} onSubmit={handleSubmit}>
                <Typography {...styles.labelTypography}>{translate("Promo Code")}</Typography>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        {...styles.promotionCodeTextField}
                        placeholder={translate("Add Promo Code")}
                        value={promotionCode}
                        onChange={handlePromotionCodeChange}
                        data-test-selector="promotionCodeTextField"
                    />
                    <Button
                        {...styles.applyButton}
                        type="submit"
                        disabled={isApplyButtonDisabled || promotionCode.length === 0}
                        data-test-selector="applyPromotionCodeButton"
                    >
                        {translate("Apply")}
                    </Button>
                </StyledWrapper>
                {promotionErrorMessage && <Typography {...styles.errorTypography}>{promotionErrorMessage}</Typography>}
                {/* XNG-Changes: put message down */}
                {promotionSuccessMessage && (
                    <Typography {...styles.applySuccessText} as="div">
                        {promotionSuccessMessage}
                    </Typography>
                )}
            </StyledForm>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitPromotionCode),
    definition: {
        group: "Common",
        allowedContexts: ["CartPage", "CheckoutShippingPage", "CheckoutReviewAndSubmitPage", "OnePageCheckoutPage"],
        displayName: "Promotion Code Form",
    },
};

export default widgetModule;
